import clone from 'clone'
import { mapState } from 'pinia'

import { usePageStore } from '../stores/page'
import imgSrcSet from './img-src-set'
import pagination from './pagination'

const mixin = {
  mixins: [imgSrcSet, pagination],
  // head () { // this is not called in RSv2 therefore it is moved to PageWrapper where the mixin is used
  //   return this.generateMetas()
  // },
  mounted () {
    window.addEventListener('pageshow', (event) => {
      if (event.persisted) {
        this.reloadConsentScript()
      }
    })
  },
  computed: {
    ...mapState(usePageStore, ['isArticleOrArticleVersion']),
    title () {
      const pageSuffix = this.pageData?.pageMeta?.page > 1 ? ` - Seite ${this.pageData.pageMeta.page}` : ''
      const metaTitle = this.pageData?.metaData?.metaTitle
      const headline = this.pageData?.body?.headline
      // rsConfig is from configStore and imported by component that uses this mixin
      const brand = this.rsConfig.actualBrandName
      const pipedBrandSuffix = this.rsConfig.appendBrandToMetaTitle ? ` | ${brand}` : ''
      return metaTitle
        ? `${metaTitle}${pageSuffix}${pipedBrandSuffix}`
        : (headline ? `${headline}${pageSuffix}${pipedBrandSuffix}` : (brand + pageSuffix))
    },
    renderVwoScript () {
      return this.consentsExist
    }
  },
  methods: {
    handleSearchParamsPoly (urlNode = null, action = null, key = null, value = null) {
      const params = {}
      // get existing search params first
      urlNode.search.replace(/[?&]+([^=&]+)=([^&]*)/gi, function (s, k, v) { params[k] = v })

      // set or delete single param
      if (action === 'set' && key !== null) {
        params[key] = value
      } else if (action === 'delete' && params[key] !== undefined) {
        delete params[key]
      }

      // set new search params
      urlNode.search = Object.keys(params).map((k) => {
        return encodeURIComponent(k) + '=' + encodeURIComponent(params[k])
      }).join('&')

      return null
    },
    extractMetas () {
      const metas = []

      if (this.pageData && this.pageData.metaData) {
        const {
          metaTitle,
          metaDescription,
          ogTitle,
          ogDescription,
          ogImage,
          noIndex,
          noIndexFollow
        } = this.pageData.metaData
        if (metaTitle) {
          metas.push(
            {
              name: 'title',
              'data-vmid': 'title',
              content: this.title
            }
          )
        }
        if (metaDescription) {
          metas.push(
            {
              name: 'description',
              'data-vmid': 'description',
              content: metaDescription
            }
          )
        }
        if (ogTitle) {
          metas.push({
            property: 'og:title',
            'data-vmid': 'og-title',
            content: ogTitle
          })
        }
        if (ogDescription) {
          metas.push({
            property: 'og:description',
            'data-vmid': 'og-description',
            content: ogDescription
          })
        }
        if (ogImage) {
          const ogImageObject = this.createImgSrc({
            image: ogImage,
            brand: this.brand,
            setName: 'ogImage'
          })
          metas.push({
            property: 'og:image',
            'data-vmid': 'og-image',
            content: ogImageObject
          })
        }
        const robotsContentMaxImagePreview = this.isArticleOrArticleVersion ? ',max-image-preview:large' : ''
        if (noIndex) {
          metas.push({
            name: 'robots',
            'data-vmid': 'robots',
            content: `noindex${robotsContentMaxImagePreview}${noIndexFollow ? ',follow' : ''}`
          })
        } else {
          metas.push({
            name: 'robots',
            'data-vmid': 'robots',
            content: `index,follow${robotsContentMaxImagePreview}`
          })
        }
      }

      if (this.pageData.pageMeta) {
        const {
          pageType,
          composerVersion
        } = this.pageData.pageMeta

        if (pageType === this.isArticleOrArticleVersion) {
          metas.push({
            property: 'og:type',
            content: 'article'
          })
        }
        if (composerVersion) {
          metas.push({
            name: 'generator',
            'data-vmid': 'generator-cs',
            content: 'composer v' + composerVersion
          })
        }
        metas.push({
          name: 'generator',
          'data-vmid': 'generator-rs',
          content: 'renderer v' + this.rsVersion
        })
      }

      return metas
    },
    evaluateMetas () {
      return [...clone(this.rsConfig.htmlHeadMetas), ...this.extractMetas()]
    },
    generateMetas () {
      let setHtmlHeadScriptSources = []
      // consentConfig is from sourcepointStore and imported by component that uses this mixin
      if (!this.consentConfig.isConsentWhitelistPage) {
        setHtmlHeadScriptSources = clone(this.rsConfig.htmlHeadScriptSources) || []
      }

      let headLinks = clone(this.rsConfig.htmlHeadLinks)

      // append version to css files and their preloads
      headLinks = headLinks.map(element => {
        if (element.appendVersion) {
          delete element.appendVersion // we do not want this property to get rendered
          return {
            ...element,
            href: `${element.href}?v=${this.rsVersion}`
          }
        } else {
          return element
        }
      })

      const head = {
        htmlAttrs: { lang: 'de' },
        title: this.title,
        meta: this.evaluateMetas(),
        link: headLinks,
        script: setHtmlHeadScriptSources
      }
      // ugly workaround needed to actually render quotes (") instead of &quot;
      // see more -> https://github.com/declandewet/vue-meta/issues/247
      head.__dangerouslyDisableSanitizers = ['script']
      if (this.pageData && this.pageData.metaData && this.pageData.metaData.structuredData) {
        this.pageData.metaData.structuredData.forEach((elem, index) => {
          const structuredData = {
            innerHTML: JSON.stringify(elem),
            'data-vmid': 'structured-data',
            type: 'application/ld+json'
          }
          head.script.push(structuredData)
        })
      }

      // NOTE got mixed up with rendering-v2, but was still a draft and should not have gone on production: https://gitlab.com/xcel-next/next-level/-/merge_requests/5903#note_2091575011
      // Prerendering with speculatively fetched data. Documentation: https://developer.chrome.com/docs/web-platform/prerender-pages
      // head.script.push({
      //   'data-vmid': 'prerender-pages',
      //   type: 'speculationrules',
      //   innerHTML: `{
      //       "prerender": [{
      //         "where": { "href_matches": "/*" },
      //         "eagerness": "moderate"
      //       }],
      //       "prefetch": [{
      //         "where": { "not": { "href_matches": "/*" } },
      //         "eagerness": "moderate"
      //       }]
      //     }`
      // })

      // prep canonical and prev/next links
      const { baseUrl, page, numPages, teaserFilter } = this.pageData?.pageMeta || {}
      const { canonical } = this.pageData?.metaData || {}

      // Canonical Url and prev / next links
      if (canonical) {
        head.link.push({
          rel: 'canonical',
          'data-vmid': 'canonical',
          href: canonical
        })

        if (page && numPages) {
          if (page > 1) {
            const prev = new URL(canonical, baseUrl)
            if (page === 2) {
              // page 1 refers to the index page so we remove the parameter
              this.handleSearchParamsPoly(prev, 'delete', 'page')
            } else {
              if (teaserFilter) {
                prev.searchParams.set('filtered', teaserFilter)
              }

              this.handleSearchParamsPoly(prev, 'set', 'page', page - 1)
            }
            head.link.push({
              'data-vmid': 'prev',
              rel: 'prev',
              href: prev.href
            })
          }
          if (numPages > page) {
            const next = new URL(canonical, baseUrl)
            if (teaserFilter) {
              next.searchParams.set('filtered', teaserFilter)
            }

            this.handleSearchParamsPoly(next, 'set', 'page', page + 1)
            head.link.push({
              'data-vmid': 'next',
              rel: 'next',
              href: next.href
            })
          }
        }
      }

      // AB Testing - VWO
      // *********** IMPORTANT *********** These scripts must be fairly high up in the head section. having them here, they will appear just after the consent scripts
      if (this.renderVwoScript) {
        head.script.unshift({
          'data-vmid': 'vwo-code',
          id: 'vwoCode',
          type: 'text/javascript',
          innerHTML: '<!-- Start VWO Async SmartCode -->\n' +
                    'window._vwo_code || (function() {\n' +
                    'var account_id=895240,\n' +
                    'version=2.1,\n' +
                    'settings_tolerance=2000,\n' +
                    'hide_element=\'\',\n' + // hide_element='body' => set '' because we do not flickering
                    'hide_element_style=\'\',\n' + // opacity:0 !important;filter:alpha(opacity=0) !important;background:none !important  => set '' because we do not flickering
                    '/* DO NOT EDIT BELOW THIS LINE */\n' +
                    'f=false,w=window,d=document,v=d.querySelector(\'#vwoCode\'),cK=\'_vwo_\'+account_id+\'_settings\',cc={};try{var c=JSON.parse(localStorage.getItem(\'_vwo_\'+account_id+\'_config\'));cc=c&&typeof c===\'object\'?c:{}}catch(e){}var stT=cc.stT===\'session\'?w.sessionStorage:w.localStorage;code={use_existing_jquery:function(){return typeof use_existing_jquery!==\'undefined\'?use_existing_jquery:undefined},library_tolerance:function(){return typeof library_tolerance!==\'undefined\'?library_tolerance:undefined},settings_tolerance:function(){return cc.sT||settings_tolerance},hide_element_style:function(){return\'{\'+(cc.hES||hide_element_style)+\'}\'},hide_element:function(){if(performance.getEntriesByName(\'first-contentful-paint\')[0]){return\'\'}return typeof cc.hE===\'string\'?cc.hE:hide_element},getVersion:function(){return version},finish:function(e){if(!f){f=true;var t=d.getElementById(\'_vis_opt_path_hides\');if(t)t.parentNode.removeChild(t);if(e)(new Image).src=\'https://dev.visualwebsiteoptimizer.com/ee.gif?a=\'+account_id+e}},finished:function(){return f},addScript:function(e){var t=d.createElement(\'script\');t.type=\'text/javascript\';if(e.src){t.src=e.src}else{t.text=e.text}d.getElementsByTagName(\'head\')[0].appendChild(t)},load:function(e,t){var i=this.getSettings(),n=d.createElement(\'script\'),r=this;t=t||{};if(i){n.textContent=i;d.getElementsByTagName(\'head\')[0].appendChild(n);if(!w.VWO||VWO.caE){stT.removeItem(cK);r.load(e)}}else{var o=new XMLHttpRequest;o.open(\'GET\',e,true);o.withCredentials=!t.dSC;o.responseType=t.responseType||\'text\';o.onload=function(){if(t.onloadCb){return t.onloadCb(o,e)}if(o.status===200){_vwo_code.addScript({text:o.responseText})}else{_vwo_code.finish(\'&e=loading_failure:\'+e)}};o.onerror=function(){if(t.onerrorCb){return t.onerrorCb(e)}_vwo_code.finish(\'&e=loading_failure:\'+e)};o.send()}},getSettings:function(){try{var e=stT.getItem(cK);if(!e){return}e=JSON.parse(e);if(Date.now()>e.e){stT.removeItem(cK);return}return e.s}catch(e){return}},init:function(){if(d.URL.indexOf(\'__vwo_disable__\')>-1)return;var e=this.settings_tolerance();w._vwo_settings_timer=setTimeout(function(){_vwo_code.finish();stT.removeItem(cK)},e);var t;if(this.hide_element()!==\'body\'){t=d.createElement(\'style\');var i=this.hide_element(),n=i?i+this.hide_element_style():\'\',r=d.getElementsByTagName(\'head\')[0];t.setAttribute(\'id\',\'_vis_opt_path_hides\');v&&t.setAttribute(\'nonce\',v.nonce);t.setAttribute(\'type\',\'text/css\');if(t.styleSheet)t.styleSheet.cssText=n;else t.appendChild(d.createTextNode(n));r.appendChild(t)}else{t=d.getElementsByTagName(\'head\')[0];var n=d.createElement(\'div\');n.style.cssText=\'z-index: 2147483647 !important;position: fixed !important;left: 0 !important;top: 0 !important;width: 100% !important;height: 100% !important;background: white !important;\';n.setAttribute(\'id\',\'_vis_opt_path_hides\');n.classList.add(\'_vis_hide_layer\');t.parentNode.insertBefore(n,t.nextSibling)}var o=\'https://dev.visualwebsiteoptimizer.com/j.php?a=\'+account_id+\'&u=\'+encodeURIComponent(d.URL)+\'&vn=\'+version;if(w.location.search.indexOf(\'_vwo_xhr\')!==-1){this.addScript({src:o})}else{this.load(o+\'&x=true\')}}};w._vwo_code=code;code.init();})();\n' +
                    '<!-- End VWO Async SmartCode -->'
        })
      }

      // *********** IMPORTANT *********** These scripts must be the first scripts to load and in this order!!!!
      // Sourcepoint needs to be loaded for additional consent (embeds) and to open the consent manager on the datenschutz page
      // even if consent is not really required with PUR abo. For these cases we just hide the popup with the `hidePrivacyManager` setting below
      // consentConfig is from sourcepointStore and imported by component that uses this mixin
      // Only needed to display the data protection manager manually
      head.script.unshift({
        'data-vmid': 'open-privacy-manager',
        type: 'text/javascript',
        async: false,
        innerHTML: `window.openPrivacyManager = function() {
            window._sp_.loadPrivacyManagerModal(${this.consentConfig.privacyManagerId})
          }`
      })
      head.script.unshift({
        'data-vmid': 'wrapper-without-detection',
        type: 'text/javascript',
        async: false,
        src: this.consentConfig.wrapperWithoutDetection
      })
      head.script.unshift({
        'data-vmid': 'sp-config',
        type: 'text/javascript',
        async: false,
        innerHTML: `
          window._sp_ = {
            config: {
              accountId: ${this.consentConfig.accountId},
              propertyId: ${this.consentConfig.propertyId},
              baseEndpoint: '${this.consentConfig.privacyBaseEndpoint}',
              targetingParams: {
                hidePrivacyManager: ${this.hasPurSub || this.consentConfig.isConsentWhitelistPage}
              }
            }
          }`
      })
      head.script.unshift({
        'data-vmid': 'gdpr-stub',
        type: 'text/javascript',
        async: false,
        src: '/js/gdpr-stub.js'
      })

      // Preload GDPR stuff from/for Sourcepoint
      head.link.unshift({
        'data-vmid': 'wrapper-without-detection-preload',
        rel: 'preload',
        as: 'script',
        href: this.consentConfig.wrapperWithoutDetection
      })
      head.link.unshift({
        'data-vmid': 'gdpr-stub-preload',
        rel: 'preload',
        as: 'script',
        href: '/js/gdpr-stub.js'
      })

      return head
    },
    reloadConsentScript () {
      // Remove existing consent script
      document.querySelector('[data-vmid="wrapper-without-detection"]')?.remove()
      // re-add script
      const script = document.createElement('script')
      script.src = `${this.consentConfig.wrapperWithoutDetection}`
      script.async = true
      script.dataset.vmid = 'wrapper-without-detection'
      document.head.appendChild(script)
    }
  }
}

export default mixin
